/**
 * QuickAddUsersContext - lets any people picker open the one Quick Add User modal.
 *
 * The modal (an organism) is mounted once, high in the tree, by
 * QuickAddUsersProvider. The pickers that offer it — the assignee fieldsets,
 * the hours editors, the contact picker — are molecules, which must not
 * import an organism. They reach it through this context instead:
 *
 *   const { canQuickAdd, openQuickAdd } = useQuickAddUsers();
 *   openQuickAdd({ defaultCompanyId, initialName, onCreated: addRows });
 *
 * `canQuickAdd` is false when the viewer's role can't create users, or when no
 * provider is mounted (isolated component tests), so a picker can simply hide
 * its link and dropdown action.
 */
import { createContext, useContext } from 'react';
import type { QuickAddedPersonRef } from '../utils/quickAddUser';

/** One request to open the modal. */
export interface QuickAddRequest {
	/** The work order's business — pre-selected as each person's primary business. */
	defaultCompanyId?: string | number | null;
	/** Text typed into a search box, pre-filled as the first person's name. */
	initialName?: string;
	/** One person only (e.g. picking a single contact) — hides "+ Add another person". */
	single?: boolean;
	/** Receives everyone the modal saves, as soon as they are saved. */
	onCreated: (people: QuickAddedPersonRef[]) => void;
}

export interface QuickAddUsersContextValue {
	/** True when the viewer may create people here. */
	canQuickAdd: boolean;
	/** Open the modal for a request. A no-op when canQuickAdd is false. */
	openQuickAdd: (request: QuickAddRequest) => void;
}

/** Default: nothing mounted, nothing offered. */
export const QuickAddUsersContext = createContext<QuickAddUsersContextValue>({
	canQuickAdd: false,
	openQuickAdd: () => undefined,
});

/** Read the Quick Add context. */
export function useQuickAddUsers(): QuickAddUsersContextValue {
	return useContext(QuickAddUsersContext);
}
